import React from 'react';
import type { HighlightRange } from '@/features/quiz/utils/highlightRanges';

/**
 * Text with its `token`-th whitespace token carried on the gold wash — the
 * karaoke highlight for Pam's narration — AND/OR any user-marked
 * `markedRanges` carried on a persistent highlighter wash (the ASWB/
 * Prometric-style "mark this text" study aid, see
 * `src/features/quiz/utils/highlightRanges.ts`). Renders the EXACT same
 * characters as a plain text node either way (whitespace runs preserved,
 * textContent unaffected), so screen readers, copy/paste, and byte-identical
 * rendering when neither feature is active all keep working unchanged.
 *
 * The two highlight sources are independent — narration is transient
 * (`token` changes as Pam reads) and marks are persistent (`markedRanges`
 * survive the whole question) — but they share this one render pass so a
 * word never ends up double-wrapped. If a word is both the current narration
 * token AND user-marked, narration wins visually (it's momentary and needs
 * to stand out); the mark itself is untouched and reappears once narration
 * moves on.
 *
 * `struckRanges` is the marker's twin gesture (the ASWB exam guide allows
 * highlighting AND striking through stem text): rendered as a real <s> so the
 * line-through survives copy/paste and reads as struck to a screen reader. The
 * two user gestures are exclusive over a span, enforced by the caller.
 *
 * Gold pair on purpose: `celebrate-50` is the app's soft-attention surface and
 * `on-celebrate` is its AA-passing text color; teal/plum stay reserved for
 * selection and Pam-the-coach respectively. User marks use `warning-200` — a
 * real highlighter yellow, already in the palette (used for hints), and
 * visually distinct from narration's gold so the two never read as one thing.
 */
export const HighlightedText: React.FC<{
  text: string;
  token: number | null;
  markedRanges?: HighlightRange[];
  struckRanges?: HighlightRange[];
}> = ({ text, token, markedRanges = [], struckRanges = [] }) => {
  if (token === null && markedRanges.length === 0 && struckRanges.length === 0) return <>{text}</>;

  const pieces = text.split(/(\s+)/); // separators preserved
  let wordIndex = 0;
  let charOffset = 0;
  return (
    <>
      {pieces.map((piece, i) => {
        const start = charOffset;
        charOffset += piece.length;

        if (piece === '' || /^\s+$/.test(piece)) return <React.Fragment key={i}>{piece}</React.Fragment>;

        const end = charOffset;
        const isCurrent = wordIndex === token;
        const overlaps = (range: HighlightRange) => start < range.end && end > range.start;
        const isMarked = markedRanges.some(overlaps);
        const isStruck = struckRanges.some(overlaps);
        wordIndex += 1;

        if (isCurrent) {
          return (
            <mark key={i} className="rounded bg-celebrate-50 px-0.5 text-on-celebrate">
              {piece}
            </mark>
          );
        }
        if (isStruck) {
          return (
            <s key={i} className="text-neutral-500 line-through">
              {piece}
            </s>
          );
        }
        if (isMarked) {
          return (
            <mark key={i} className="rounded bg-warning-200 px-0.5">
              {piece}
            </mark>
          );
        }
        return <React.Fragment key={i}>{piece}</React.Fragment>;
      })}
    </>
  );
};
